'use client'

import React, { useEffect, useMemo, useState, Suspense, useCallback, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { AlertCircle } from 'lucide-react'

import { useToast } from '@/hooks/use-toast'
import { createBrowserClient } from '@/supabase'

import { LoadingState } from '@/components/ui/loading-state'
import { EmptyState } from '@/components/ui/empty-state'
import { Button } from '@/components/ui/button'
import { OnboardingProgress } from '@/components/onboarding/OnboardingProgress'

import { ProjectWizard } from './ProjectWizard'
import { onboardingSteps } from './constants'
import { projectFormSchema } from './schemas'
import { useProjectFormStore } from '@/stores/useProjectFormStore'
import { trackLandingEvent } from '@/lib/landing-analytics'

/** ---------- type guards ---------- */
const hasClientUserId = (value: unknown): value is { user_id: string } => {
  return Boolean(value && typeof value === 'object' && 'user_id' in value)
}

const hasCounselId = (value: unknown): value is { counsel_id: number } => {
  return Boolean(value && typeof value === 'object' && 'counsel_id' in value)
}

/** ---------- UI shell ---------- */
function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="w-full">{children}</div>
}

/** ---------- types ---------- */
interface FormData {
  projectDomain: string
  neededRoles: string[]
  projectServiceName: string
  shortSummary: string
  projectType?: string
  resultPreset?: string
  collaborationMode?: 'TEAM' | 'INDIVIDUAL'
  functionality: string
  period: string
  cost: string
  skills: string[]
  contactPhone: string
  contactEmail: string
  mainCategories: string[]
  subCategories: string[]
  referenceService: string
  materials: string[]
  designLevel: string
  engagementType: string
  weeklyHours: string
  otherRequirements: string
  startDate: string
  workType: string
}

type UploadedFile = { url: string; name: string; size: number; type: string }


const normalizePhoneNumber = (raw: string) => {
  const digits = raw.replace(/\D/g, '')
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
}

const initialFormData: FormData = {
  projectDomain: '',
  neededRoles: [],
  projectServiceName: '',
  shortSummary: '',
  projectType: undefined,
  resultPreset: undefined,
  collaborationMode: 'TEAM',
  functionality: '',
  period: '',
  cost: '',
  skills: [],
  contactPhone: '',
  contactEmail: '',
  mainCategories: [],
  subCategories: [],
  referenceService: '',
  materials: [],
  designLevel: '',
  engagementType: '',
  weeklyHours: '',
  otherRequirements: '',
  startDate: '',
  workType: '',
}

function extractZodMessages(result: ReturnType<typeof projectFormSchema.safeParse>): string[] {
  if (result.success) return []
  const fieldErrors = result.error.flatten().fieldErrors
  const messages: string[] = []
  for (const key of Object.keys(fieldErrors)) {
    const arr = fieldErrors[key as keyof typeof fieldErrors]
    if (arr && arr[0]) messages.push(arr[0])
  }
  return messages
}

/** ---------- main content ---------- */
const ProjectCounselFormContent: React.FC = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { toast } = useToast()

  const supabase = useMemo(() => (typeof window !== 'undefined' ? createBrowserClient() : null), [])
  const isOnboarding = searchParams.get('from') === 'onboarding'

  const [authChecking, setAuthChecking] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)

  const [loading, setLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const viewTrackedRef = useRef(false)

  // Store에서 formData 가져오기
  const { formData: storeFormData, updateFormData, resetForm } = useProjectFormStore()
  const [formData, setFormData] = useState<FormData>(storeFormData)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const prefillAppliedRef = useRef(false)
  const [clientContactInfo, setClientContactInfo] = useState<{
    contact_phone: string | null
    email: string | null
  } | null>(null)

  // Store의 formData가 변경되면 로컬 state도 업데이트
  useEffect(() => {
    setFormData(storeFormData)
  }, [storeFormData])

  // 등록 페이지 진입 이벤트 (퍼널 시작점)
  useEffect(() => {
    if (viewTrackedRef.current) return
    viewTrackedRef.current = true

    const source = searchParams.get('from') || (searchParams.get('forcePrefill') === '1' ? 'landing_quick_start' : 'direct')
    trackLandingEvent('projects_new_view', {
      source,
      has_main_category_param: Boolean(searchParams.get('mainCategory')),
      has_brief_param: Boolean(searchParams.get('brief')),
    })
  }, [searchParams])

  // 랜딩 quick start 쿼리(mainCategory/brief)를 최초 1회 폼에 반영
  useEffect(() => {
    if (prefillAppliedRef.current) return

    const mainCategoryParam = searchParams.get('mainCategory')
    const briefParam = searchParams.get('brief')?.trim()
    const forcePrefill = searchParams.get('forcePrefill') === '1'

    if (!mainCategoryParam && !briefParam) {
      prefillAppliedRef.current = true
      return
    }

    const nextData: Partial<FormData> = {}
    const hasMainCategory = storeFormData.mainCategories.length > 0

    if (mainCategoryParam && (forcePrefill || !hasMainCategory)) {
      nextData.mainCategories = [mainCategoryParam]
    }

    if (briefParam) {
      if (forcePrefill || !storeFormData.projectServiceName) {
        nextData.projectServiceName = briefParam.slice(0, 60)
      }
      if (forcePrefill || !storeFormData.functionality) {
        nextData.functionality = briefParam
      }
    }

    if (Object.keys(nextData).length > 0) {
      setFormData((prev) => ({ ...prev, ...nextData }))
      updateFormData(nextData)
      trackLandingEvent('projects_new_prefill_applied', {
        force_prefill: forcePrefill,
        applied_main_category: Boolean(nextData.mainCategories?.length),
        applied_brief: Boolean(nextData.functionality),
      })
    }

    prefillAppliedRef.current = true
  }, [searchParams, storeFormData, updateFormData])

  /** 권한 체크 */
  useEffect(() => {
    if (!supabase) return

    const checkAuthAndRole = async () => {
      try {
        setAuthChecking(true)

        const {
          data: { user },
        } = await supabase.auth.getUser()

        if (!user) {
          router.push('/auth?redirect=/client/projects/new&role=client')
          return
        }

        const { data: clientData } = await supabase
          .from('client')
          .select('user_id, company_name, contact_person, contact_phone, email')
          .eq('user_id', user.id)
          .maybeSingle()

        if (!hasClientUserId(clientData)) {
          router.push('/onboarding/enterprise/company')
          return
        }

        const clientDataTyped = clientData as any
        if (!clientDataTyped.company_name || !clientDataTyped.contact_person || !clientDataTyped.contact_phone) {
          router.push('/onboarding/enterprise/company')
          return
        }

        // 클라이언트 연락처 정보 저장
        setClientContactInfo({
          contact_phone: clientDataTyped.contact_phone || null,
          email: clientDataTyped.email || null,
        })

        // 클라이언트 정보가 있으면 formData에 자동으로 채워넣기
        const contactData = {
          contactPhone: clientDataTyped.contact_phone ? normalizePhoneNumber(clientDataTyped.contact_phone) : storeFormData.contactPhone,
          contactEmail: clientDataTyped.email || storeFormData.contactEmail,
        }
        setFormData((prev) => ({ ...prev, ...contactData }))
        // Store에도 저장
        updateFormData(contactData)

        setAuthError(null)
      } catch (e) {
        console.error('권한 확인 실패:', e)
        setAuthError('권한을 확인하는 중 오류가 발생했습니다.')
      } finally {
        setAuthChecking(false)
      }
    }

    checkAuthAndRole()
  }, [router, supabase])

  const handleInputChange = useCallback((field: keyof FormData, value: string | string[] | undefined) => {
    const newData = { [field]: value as any }
    setFormData((prev) => ({ ...prev, ...newData }))
    // Store에도 즉시 저장
    updateFormData(newData)
  }, [updateFormData])

  const handleFileChange = useCallback((files: File[]) => {
    setSelectedFiles(files)
  }, [])

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSubmit = useCallback(async (): Promise<number | null> => {
    if (!supabase) return null
    if (loading || isSubmitted) return null

    // 1) validate
    const validation = projectFormSchema.safeParse(formData)
    if (!validation.success) {
      const messages = extractZodMessages(validation)
      toast({
        title: '입력 정보를 확인해주세요',
        description: messages.slice(0, 6).join('\n'),
        variant: 'destructive',
      })
      return null
    }

    setLoading(true)

    try {
      // 2) session
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) {
        toast({ title: '로그인이 필요합니다', variant: 'destructive' })
        router.push('/auth?role=client')
        return null
      }

      // 3) client check
      const { data: clientData } = await supabase.from('client').select('user_id').eq('user_id', user.id).single()

      if (!hasClientUserId(clientData)) {
        toast({ title: '기업 계정이 아닙니다', variant: 'destructive' })
        return null
      }

      const sanitizeFileName = (name: string) =>
        name
          .trim()
          .replace(/\s+/g, '-')
          .replace(/[^a-zA-Z0-9.\-]/g, '_')

      // 4) upload attachments
      let uploadedFiles: UploadedFile[] = []
      if (selectedFiles.length > 0) {
        const basePath = `counsel/${clientData.user_id}/${Date.now()}`
        const uploadResults = await Promise.all(
          selectedFiles.map(async (file, index) => {
            const sanitizedName = sanitizeFileName(file.name)
            const filePath = `${basePath}/${index}-${sanitizedName}`

            const { error: uploadError } = await supabase.storage.from('attach_file').upload(filePath, file, {
              cacheControl: '3600',
              upsert: false,
            })

            if (uploadError) throw uploadError

            const {
              data: { publicUrl },
            } = supabase.storage.from('attach_file').getPublicUrl(filePath)

            return { url: publicUrl, name: file.name, size: file.size, type: file.type || '' } as UploadedFile
          })
        )

        uploadedFiles = uploadResults.filter(Boolean) as UploadedFile[]
      }

      // 5) insert counsel
      const payload = {
        client_id: clientData.user_id,
        title: formData.projectServiceName,
        summary: formData.shortSummary || null,
        domain: formData.projectDomain,
        needed_roles: formData.neededRoles.length > 0 ? formData.neededRoles : null,
        outline: formData.functionality,
        period: formData.period as any,
        cost: formData.cost as any,
        contact_phone: formData.contactPhone || null,
        contact_email: formData.contactEmail || null,
        field: formData.mainCategories.length > 0 ? formData.mainCategories : null,
        counsel_type: formData.subCategories.length > 0 ? formData.subCategories : null,
        skill: formData.skills.length > 0 ? formData.skills : null,
        work_type: formData.workType || null,
        reference_service: formData.referenceService || null,
        output: formData.otherRequirements || null,
        start_date: formData.startDate || null,
        project_type: formData.projectType || null,
        design_level: formData.designLevel,
        engagement_type: formData.engagementType,
        weekly_hours: formData.weeklyHours || null,
        materials: formData.materials.length > 0 ? formData.materials : null,
        requirements_json: {
          projectDomain: formData.projectDomain,
          neededRoles: formData.neededRoles,
          shortSummary: formData.shortSummary,
          resultPreset: formData.resultPreset || null,
          collaborationMode: formData.collaborationMode || 'TEAM',
          designLevel: formData.designLevel,
          engagementType: formData.engagementType,
          weeklyHours: formData.weeklyHours || null,
          materials: formData.materials,
        },
        counsel_status: 'pending',
      } as any

      const { data, error } = await supabase.from('counsel').insert(payload).select().single()
      if (error) throw error
      if (!hasCounselId(data)) throw new Error('상담 생성 결과를 확인할 수 없습니다.')

      // 6) async notifications (best-effort)
      if ((data as any).counsel_status === 'pending' && !(data as any).requested_team_id) {
        import('@/utils/counsel-matching-notifications')
          .then((module) => module.createTeamMatchingNotifications(supabase, (data as any).counsel_id))
          .catch((err) => console.error('[ProjectCounselFormContent] 매칭 알림 생성 실패:', err))
      }

      // 7) save attachment metadata (best-effort)
      if (uploadedFiles.length > 0) {
        const payloadFiles = uploadedFiles.map((f) => ({
          counsel_id: (data as any).counsel_id,
          url: f.url,
          file_name: f.name,
          file_size: f.size,
          mime_type: f.type,
        }))

        const { error: attachError } = await (supabase as any).from('counsel_attachments').insert(payloadFiles as any)
        if (attachError) {
          console.error('첨부 저장 실패:', attachError.message)
          toast({
            title: '첨부 저장 실패',
            description: '상담은 등록되었지만 첨부 파일 저장에 실패했습니다. 다시 시도해주세요.',
            variant: 'destructive',
          })
        }
      }

      setIsSubmitted(true)
      toast({ title: '상담이 등록되었습니다', description: '요청이 정상적으로 접수되었습니다.' })
      trackLandingEvent('project_registration_completed', {
        source: searchParams.get('from') || (searchParams.get('forcePrefill') === '1' ? 'landing_quick_start' : 'direct'),
        is_onboarding: isOnboarding,
        has_attachments: selectedFiles.length > 0,
      })

      // 온보딩 플로우인 경우 is_onboarded 플래그 업데이트 및 대시보드로 이동
      if (isOnboarding) {
        await supabase
          .from('accounts')
          .update({ is_onboarded: true })
          .eq('user_id', user.id)
          .eq('profile_type', 'COMPANY')
          .eq('is_active', true)
          .is('deleted_at', null)
        
        // 제출 완료 시 store 초기화
        resetForm()
        
        // 온보딩인 경우 대시보드로 직접 이동
        router.push('/client/dashboard')
        return null
      }

      // 제출 완료 시 store 초기화 및 상담 상세로 이동
      resetForm()
      const counselId = (data as any).counsel_id as number
      router.push(`/client/projects/${counselId}`)
      return counselId
    } catch (e: any) {
      console.error('상담 신청 실패:', e)
      toast({
        title: '상담 신청에 실패했습니다',
        description: '잠시 후 다시 시도해주세요.',
        variant: 'destructive',
      })
      return null
    } finally {
      setLoading(false)
    }
  }, [supabase, loading, isSubmitted, formData, selectedFiles, toast, router, searchParams, isOnboarding, resetForm])

  // 권한 체크 중
  if (authChecking) {
    return (
      <PageShell>
        <LoadingState message="권한을 확인하는 중입니다..." />
      </PageShell>
    )
  }

  // 권한 에러
  if (authError) {
    return (
      <PageShell>
        <EmptyState
          icon={AlertCircle}
          title="접근 권한이 없습니다"
          description={authError}
          action={
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => router.push('/auth?redirect=/client/projects/new&role=client')}>
                기업 계정으로 로그인
              </Button>
              <Button onClick={() => router.push('/')} variant="outline">
                홈으로 이동
              </Button>
            </div>
          }
        />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="space-y-8">
        {/* 온보딩 진행 표시 (온보딩일 때만) */}
        {isOnboarding && (
          <div className="bg-white">
            <OnboardingProgress
              currentStep={2}
              totalSteps={onboardingSteps.length}
              steps={onboardingSteps as unknown as Array<{ label: string; path: string }>}
            />
          </div>
        )}

        {/* 폼 */}
        <div className="bg-white">
          <ProjectWizard
            formData={formData}
            selectedFiles={selectedFiles}
            onChange={handleInputChange}
            onFileChange={handleFileChange}
            onRemoveFile={handleRemoveFile}
            onSubmit={handleSubmit}
            loading={loading}
            isOnboarding={isOnboarding}
            clientContactInfo={clientContactInfo}
          />
        </div>
      </div>
    </PageShell>
  )
}

/** ---------- suspense wrapper ---------- */
const ProjectCounselForm: React.FC = () => {
  return (
    <Suspense
      fallback={
        <PageShell>
          <LoadingState message="로딩 중..." />
        </PageShell>
      }
    >
      <ProjectCounselFormContent />
    </Suspense>
  )
}

export default ProjectCounselForm


